import React, { useCallback, useState } from 'react';

import { defineMessages, useIntl } from 'react-intl';

import { Box, Stack } from '@atlaskit/primitives/compiled';
import type { HierarchyData } from '@atlassian/reporting-lines/hierarchy-chart';
import { HierarchyModal } from '@atlassian/reporting-lines/hierarchy-chart';
import { LinkList } from '@atlassian/smart-knowledge-cards/link-list';

import type { Collaborator, PersonProfileData, RecentWorkUrls } from '../../types';

import { ActionBar } from './ActionBar';
import { Collaborators } from './Collaborators';
import { ExpandableSection } from './ExpandableSection';
import { Overview } from './Overview';
import { ProfileIdentity } from './ProfileIdentity';

const messages = defineMessages({
	sectionRecentWork: {
		id: 'user-profile-preview.compact.section-recent-work',
		defaultMessage: 'Recent work',
		description: 'Expandable section header for recent work in the compact user profile preview',
	},
	sectionCollaborators: {
		id: 'user-profile-preview.compact.section-collaborators',
		defaultMessage: 'Collaborators',
		description: 'Expandable section header for collaborators in the compact user profile preview',
	},
	sectionOverview: {
		id: 'user-profile-preview.compact.section-overview',
		defaultMessage: 'Overview',
		description: 'Expandable section header for overview in the compact user profile preview',
	},
});
interface TenureData {
	beforePercent: number;
	beforeCount: number;
	afterCount: number;
}

interface CompactVariantProps {
	data: PersonProfileData;
	hierarchyData?: HierarchyData;
	recentWorkData?: RecentWorkUrls;
	collaborators?: Collaborator[];
	tenureData?: TenureData;
	onViewOrgChart?: (accountId: string) => void;
	testId?: string;
	cloudId: string;
}

export function CompactVariant({
	data,
	hierarchyData,
	recentWorkData,
	collaborators,
	tenureData,
	onViewOrgChart,
	testId,
	cloudId,
}: CompactVariantProps) {
	const intl = useIntl();
	const hasCollaborators = !!collaborators && collaborators.length > 0;
	const showRecentWorkSection = !!(recentWorkData && recentWorkData.length > 0);
	const [isHierarchyModalOpen, setIsHierarchyModalOpen] = useState(false);

	const handleOrgChartClick = useCallback(() => {
		if (hierarchyData) {
			setIsHierarchyModalOpen(true);
		}
	}, [hierarchyData]);

	const onViewOrgChartForActionBar =
		hierarchyData || onViewOrgChart ? handleOrgChartClick : undefined;

	return (
		<>
			<Stack space="space.200" testId={testId}>
				<ProfileIdentity
					data={data}
					avatarSize="xlarge"
					showDepartment
					showLocation
					showPronouns
					showLocalTime
					variant="compact"
					actions={
						<ActionBar data={data} onViewOrgChart={onViewOrgChartForActionBar} cloudId={cloudId} />
					}
				/>

				{showRecentWorkSection && (
					<ExpandableSection
						title={intl.formatMessage(messages.sectionRecentWork)}
						testId="compact-recent-work"
					>
						<Box>
							{recentWorkData && recentWorkData.length > 0 ? (
								<Box testId={testId ? `${testId}-recent-work` : undefined}>
									<LinkList urls={recentWorkData} />
								</Box>
							) : null}
						</Box>
					</ExpandableSection>
				)}

				{hasCollaborators && collaborators && (
					<ExpandableSection
						title={intl.formatMessage(messages.sectionCollaborators)}
						testId="compact-collaborators"
					>
						<Collaborators collaborators={collaborators} variant="compact" cloudId={cloudId} />
					</ExpandableSection>
				)}

				<ExpandableSection
					title={intl.formatMessage(messages.sectionOverview)}
					testId="compact-overview"
				>
					<Overview data={data} tenureData={tenureData} variant="compact" />
				</ExpandableSection>
			</Stack>

			{isHierarchyModalOpen && hierarchyData && (
				<HierarchyModal
					data={hierarchyData}
					onClose={() => setIsHierarchyModalOpen(false)}
					testId="user-profile-preview-hierarchy-modal"
				/>
			)}
		</>
	);
}
